import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, FolderKanban, Users2, IndianRupee } from "lucide-react";
import { canViewSanctioned } from "@/lib/roles";

export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", project_code: "", start_date: "", end_date: "", total_budget: 0, currency: "INR", project_manager_id: "", team_member_ids: [], funder_name: "", implementing_agency: "", schedule_vii_item: "", location: "", local_area: "Yes" });

  const load = async () => {
    const [pr, us] = await Promise.all([api.get("/projects"), api.get("/users")]);
    setProjects(pr.data); setUsers(us.data);
  };
  useEffect(() => { load(); }, []);

  const canCreate = user?.role === "Admin" || user?.role === "ProjectManager";

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, total_budget: parseFloat(form.total_budget || 0), start_date: form.start_date ? new Date(form.start_date).toISOString() : null, end_date: form.end_date ? new Date(form.end_date).toISOString() : null };
      await api.post("/projects", payload);
      toast.success("Project created");
      setOpen(false);
      setForm({ name: "", description: "", start_date: "", end_date: "", total_budget: 0, currency: "INR", project_manager_id: "", team_member_ids: [], funder_name: "", implementing_agency: "", schedule_vii_item: "", location: "", local_area: "Yes" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const toggleMember = (uid) => setForm((f) => ({
    ...f, team_member_ids: f.team_member_ids.includes(uid) ? f.team_member_ids.filter(x => x !== uid) : [...f.team_member_ids, uid]
  }));

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Portfolio</div>
          <h1 className="font-display text-3xl font-extrabold mt-1">Projects</h1>
          <p className="text-sm text-slate-500 mt-1">Create, track and govern all engagements.</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F172A] hover:bg-slate-800" data-testid="new-project-btn"><Plus className="w-4 h-4 mr-2" /> New Project</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>New Project</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name-input" /></div>
                <div>
                  <Label>Project Code <span className="text-xs text-slate-500 font-normal">(required for Budget V2 · uppercase letters/digits · e.g. MUSK, PPRG)</span></Label>
                  <Input
                    value={form.project_code}
                    onChange={(e) => setForm({ ...form, project_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
                    placeholder="e.g. MUSK"
                    maxLength={12}
                    data-testid="project-code-input"
                  />
                </div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="project-description-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                  <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
                  <div><Label>Total Budget</Label><Input type="number" value={form.total_budget} onChange={(e) => setForm({ ...form, total_budget: e.target.value })} data-testid="project-budget-input" /></div>
                  <div><Label>Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
                </div>
                <div>
                  <Label>Project Manager</Label>
                  <Select value={form.project_manager_id} onValueChange={(v) => setForm({ ...form, project_manager_id: v })}>
                    <SelectTrigger data-testid="project-pm-select"><SelectValue placeholder="Select PM" /></SelectTrigger>
                    <SelectContent>
                      {users.filter(u => ["ProjectManager", "Admin"].includes(u.role)).map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="pt-2 border-t border-slate-200">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2">CSR / UC (ICAI Annexure A) — optional</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Funder / Company Name</Label><Input value={form.funder_name} onChange={(e) => setForm({ ...form, funder_name: e.target.value })} placeholder="e.g. ABC Ltd." data-testid="project-funder-input" /></div>
                    <div><Label>Implementing Agency</Label><Input value={form.implementing_agency} onChange={(e) => setForm({ ...form, implementing_agency: e.target.value })} placeholder="e.g. RKF Trust" data-testid="project-agency-input" /></div>
                    <div className="col-span-2"><Label>Schedule VII Item</Label><Input value={form.schedule_vii_item} onChange={(e) => setForm({ ...form, schedule_vii_item: e.target.value })} placeholder="e.g. (ii) Promoting Education" data-testid="project-schedule7-input" /></div>
                    <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="State / District" data-testid="project-location-input" /></div>
                    <div>
                      <Label>Local Area?</Label>
                      <Select value={form.local_area} onValueChange={(v) => setForm({ ...form, local_area: v })}>
                        <SelectTrigger data-testid="project-localarea-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Team Members</Label>
                  <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-1">
                    {users.filter(u => u.role !== "Admin").map(u => (
                      <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-2 py-1 rounded">
                        <input type="checkbox" checked={form.team_member_ids.includes(u.id)} onChange={() => toggleMember(u.id)} />
                        <span>{u.name}</span>
                        <span className="text-xs text-slate-500">· {u.role}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <DialogFooter><Button type="submit" data-testid="save-project-btn" className="bg-[#0F172A]">Create</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {projects.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <FolderKanban className="w-12 h-12 text-slate-300 mx-auto" />
            <h3 className="mt-4 font-semibold">No projects yet</h3>
            <p className="text-sm text-slate-500">Create one to start tracking milestones, tasks, and budgets.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <Link key={p.id} to={`/app/projects/${p.id}`} data-testid={`project-card-${p.id}`}>
              <Card className="border-slate-200 hover:border-slate-400 hover:shadow-sm transition-all cursor-pointer h-full">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <Badge variant="outline" className="text-[10px] uppercase font-mono">{p.status}</Badge>
                    <div className="text-xs text-slate-500 font-mono">{p.progress ?? 0}%</div>
                  </div>
                  <h3 className="font-display font-bold text-lg leading-tight">{p.name}</h3>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2 min-h-[32px]">{p.description || "No description"}</p>
                  <Progress value={p.progress ?? 0} className="mt-3 h-1.5" />
                  <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
                    <div className="flex items-center gap-1"><Users2 className="w-3.5 h-3.5" /> {p.team_member_ids?.length || 0}</div>
                    {canViewSanctioned(user?.role) && (
                      <div className="flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5" /> {(p.total_budget || 0).toLocaleString()}</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
